import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { onQueryParamChange } from '../object-select-input/object-select-input.controller';
import { SelectOption, SingleOption, explodeOptions, isGroupedOptions, isSingleOption } from '../select-types';
import { SelectInputV2Props } from './select-input-v2.component';

interface ViewProps<T> {
    value?: string;
    onSearch: (value: string) => void;
    searchResultOptions: SelectOption<T>[];

    onSelect: (option: SingleOption<T> | null) => void;
    selected: SingleOption<T> | null;
    setSelected: React.Dispatch<React.SetStateAction<SingleOption<T> | null>>;

    valid: boolean;
    touched: boolean;
    setValue: React.Dispatch<any>;
    isValid: () => boolean;
    setTouched: React.Dispatch<React.SetStateAction<boolean>>;
}

export function filterOptions<T>(options: SelectOption<T>[], search: string): SelectOption<T>[] {
    if (!search) {
        return options;
    }

    const query = search.toLowerCase();
    return options.reduce<SelectOption<T>[]>((acc, option) => {
        if (isSingleOption(option)) {
            if (option.label.toLowerCase().includes(query)) {
                acc.push(option);
            }
            return acc;
        }

        if (isGroupedOptions(option)) {
            const matchingChildren = option.options.filter((child) => child.label.toLowerCase().includes(query));
            if (matchingChildren.length > 0) {
                acc.push({ ...option, options: matchingChildren });
            }
        }
        return acc;
    }, []);
}

const useSelectInputV2 = <T>(props: SelectInputV2Props<T>): ViewProps<T> => {
    const [value, setValue] = useState<string | undefined>(undefined);
    const [searchResultOptions, setSearchResultOptions] = useState<SelectOption<T>[]>([]);
    const [selected, setSelected] = useState<SingleOption<T> | null>(null);

    const [touched, setTouched] = useState<boolean>(false);
    const [valid, setValid] = useState<boolean>(props.valid !== undefined ? props.valid : true);
    const [searchParams, setSearchParams] = useSearchParams();

    const isValid = (): boolean => {
        let check = true;
        if ((props.value === undefined || props.value === null) && props.required === true) check = false;

        if (props.value && typeof props.value == 'string') {
            if (check === true && props.required === true && props.value.length === 0) check = false;
        } else {
            if (check === true && props.required === true && (props.value === undefined || props.value === null || props.value.length === 0)) check = false;
        }

        if (props.valid !== undefined && check === true) check = props.valid;
        setValid(check);
        return check;
    };

    useEffect(() => {
        if (props.isValidCallback) {
            props.isValidCallback(isValid());
        } else isValid();
    }, [props.value, props.valid]);

    useEffect(() => {
        if (props.searchValueOverwrite !== undefined) onSearch(props.searchValueOverwrite);
    }, [props.searchValueOverwrite]);

    useEffect(() => {
        if (!props.value) {
            setSelected(null);
            return;
        }

        const options = explodeOptions(props.options);
        const found = options.find((op) => op.value === props.value) ?? null;
        setSelected((current) => {
            if (found) return current?.value === found.value ? current : found;
            return current?.value === props.value ? current : null;
        });
    }, [props.value, props.options]);

    useEffect(() => {
        setSearchResultOptions(filterOptions(props.options || [], value ?? ''));
    }, [props.options, value]);

    useEffect(() => {
        if (props.useQueryParam) {
            onQueryParamChange(props.value ?? null, props.useQueryParam ?? '', setSearchParams);
        }
    }, [props.value, props.useQueryParam]);

    useEffect(() => {
        const queryParam = searchParams.get(props.useQueryParam ?? '');
        if (props.useQueryParam && (props.value === undefined || props.value === null) && queryParam !== null) {
            const option: SingleOption<T> | null = props.options.find((op) => isSingleOption(op) && op.value === queryParam) as SingleOption<T> | null;
            onSelect(option);
        }
    }, [props.useQueryParam, props.value, props.options, searchParams]);

    const onSelect = (option: SingleOption<T> | null) => {
        setSelected(option);
        setValue(undefined);
        props.onSelect && props.onSelect(option);
    };

    const onSearch = (value: string) => {
        setValue(value);
        props.onSearch && props.onSearch(value);
        setSearchResultOptions(filterOptions(props.options || [], value));
    };

    useEffect(() => {}, [props.value]);

    return {
        value: value,
        onSearch,
        searchResultOptions,

        onSelect,
        selected,
        setSelected,

        setTouched,
        setValue,
        isValid,
        touched,
        valid
    };
};

export default useSelectInputV2;
